import { useLayoutEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import axios from 'axios';
import { useVendorAuthStore } from '../../store/vendorAuth.ts';
import { t } from '../../lib/strings.ts';
import { Icon } from '../../components/Icon.tsx';

export function VendorLogin(): JSX.Element {
  const navigate = useNavigate();
  const login = useVendorAuthStore((s) => s.login);
  const [idCard, setIdCard] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  // Login is always shown in light theme regardless of the user's dark-mode
  // preference; their real theme is restored when navigating away.
  useLayoutEffect(() => {
    const html = document.documentElement;
    html.classList.remove('dark');
    return () => {
      try {
        const stored = localStorage.getItem('wappro.theme');
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        if (stored === 'dark' || (!stored && prefersDark)) html.classList.add('dark');
      } catch {
        /* ignore */
      }
    };
  }, []);

  async function submit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    setPending(true);
    try {
      await login(idCard.trim(), password);
      await navigate({ to: '/vendor' });
    } catch (err) {
      if (axios.isAxiosError(err) && (err.response?.status === 401 || err.response?.status === 400)) {
        setError(t.vendorPortal.errInvalid);
      } else {
        setError(t.vendorPortal.errGeneral);
      }
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-sm space-y-6">
        <div className="text-center space-y-1">
          <p className="font-serif text-2xl leading-none tracking-tight text-ink">{t.vendorPortal.brand}</p>
          <p className="mono-meta text-muted-foreground">{t.vendorPortal.brandSubtitle}</p>
        </div>

        <div className="card-paper p-6 sm:p-8 space-y-6">
          <header className="space-y-1.5 text-center">
            <h2 className="font-serif text-2xl leading-tight tracking-tight text-ink">
              {t.vendorPortal.loginTitle}
            </h2>
            <p className="text-muted-foreground text-sm">{t.vendorPortal.loginSubtitle}</p>
          </header>

          <form className="space-y-5" onSubmit={(e) => void submit(e)}>
            <div className="space-y-1.5">
              <label htmlFor="idCard" className="field-label">
                {t.vendorPortal.idCard}
              </label>
              <input
                id="idCard"
                type="text"
                required
                autoComplete="username"
                autoCorrect="off"
                spellCheck={false}
                dir="ltr"
                placeholder={t.vendorPortal.idCardPlaceholder}
                value={idCard}
                onChange={(e) => setIdCard(e.target.value)}
                className="input num"
              />
            </div>

            <div className="space-y-1.5">
              <label htmlFor="password" className="field-label">
                {t.vendorPortal.password}
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPw ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input pe-11"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((v) => !v)}
                  aria-label={showPw ? t.vendorPortal.hidePassword : t.vendorPortal.showPassword}
                  className="absolute inset-y-0 end-0 inline-flex w-11 items-center justify-center text-muted-foreground hover:text-foreground"
                >
                  <Icon name={showPw ? 'visibility_off' : 'visibility'} size={20} />
                </button>
              </div>
            </div>

            {error && (
              <div
                role="alert"
                className="rounded-2xl bg-[hsl(var(--error-container))] px-3.5 py-2.5 text-sm text-destructive"
              >
                {error}
              </div>
            )}

            <button type="submit" disabled={pending} className="btn-primary w-full py-2.5">
              {pending ? t.vendorPortal.loggingIn : <><Icon name="arrow_back" size={18} /><span>{t.vendorPortal.loginSubmit}</span></>}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
